import React, { useEffect, useState } from "react";
import { Accordion } from 'components/common';
import DetailBDSGeneralInformation from "./DetailBDSGeneralInformation"
import { useSelector } from "react-redux";
import PropTypes from 'prop-types';
import { VALUE_CLS, CHECK_DISPLAY_CLS, TH_CLS, TD_CLS, SECTION_BODY_CLS } from "../pifStyles";
import { useDiffMark, useRowMark, useDiffTitle } from "./PifDiffContext";
import DiffRichText from "./DiffRichText";

const DetailBDS1 = ({project, building, adaptation, area, areaIndex}) => {
  const {bds1} = area;
  const openWallData = bds1?.open_walls || [];
  const mark = useDiffMark();
  const rowMark = useRowMark();
  const p = (k) => `areas[${areaIndex}].bds1.${k}`;
  const rmark = (arr, item, index) => rowMark(`areas[${areaIndex}].bds1.${arr}[${item?.id}]`, `areas[${areaIndex}].bds1.${arr}[${index}]`);
  const vcls = (k) => `${VALUE_CLS}${mark(p(k))}`;
  // old → new tooltip for a highlighted field (undefined when not changed / not
  // in highlight mode, so it's inert on the plain PIF view).
  const title = useDiffTitle();
  const vtitle = (k) => title(p(k));

  const {bds1: initialKeys} = useSelector(state => state.crm_project_pif.initialKeys);
  const [activeKey, setActiveKey] = useState(initialKeys);

  useEffect(() => {
    setActiveKey(initialKeys);
  }, [initialKeys]);

  const handleAccordionSelect = (newActiveKey) => {
    setActiveKey(newActiveKey)
  }

  return (
    <Accordion
      activeKeys={activeKey}
      onChange={handleAccordionSelect}
      className="tw-text-sm"
    >
      <Accordion.Item eventKey="0" header="1. General Information">
        <div className={SECTION_BODY_CLS}>
          <DetailBDSGeneralInformation
            project={project}
            building={building}
            adaptation={adaptation}
            area={area}
          />

          <h6 className="tw-text-center tw-my-2">This area only : Page 1 of 3</h6>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-1 tw-mb-2 tw-mt-4">
            <div className="tw-col-span-5">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">Customer name:</div>
                <div className={`tw-col-span-8 tw-font-semibold ${VALUE_CLS}`}>{project.customer?.name}</div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
                <div className="tw-col-span-4">Project name:</div>
                <div className={`tw-col-span-8 tw-font-semibold ${VALUE_CLS}`}>{project.name}</div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Building usage:</div>
                <div className="tw-col-span-8 tw-font-semibold">
                  <span className={vcls('building_usage')} title={vtitle('building_usage')}>{bds1.building_usage}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-7">
              <div className="tw-flex tw-gap-x-1 tw-mb-2">
                <div>Location:</div>
                <div className={`tw-flex-1 tw-font-semibold ${VALUE_CLS}`}>{building.pj_location}</div>
              </div>
              <div className="tw-flex tw-gap-x-1 tw-mb-2 tw-items-center">
                <div>Qty of Identical Bldgs:</div>
                <div className="tw-flex-1 tw-font-semibold">
                  <span className={vcls('qty_of_identical_bldgs')} title={vtitle('qty_of_identical_bldgs')}>{bds1.qty_of_identical_bldgs}</span>
                </div>
              </div>
              <div className="tw-flex tw-gap-x-1 tw-mb-2 tw-items-center">
                <div>Estimated No. of trailers/ containers for all identical bldgs:</div>
                <div className="tw-flex-1 tw-font-semibold">
                  <span className={vcls('estimated_no_trailed')} title={vtitle('estimated_no_trailed')}>{bds1.estimated_no_trailed}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 2. Loads (kN/m2) */}
      <Accordion.Item eventKey="1" header="2. Loads (kN/m2)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-5">
              <div className="tw-grid tw-grid-cols-12 tw-mb-2 tw-items-center">
                <div className="tw-col-span-5">Live (On Purlins):</div>
                <div className="tw-col-span-7">
                  <div className="tw-flex">
                    <span className={vcls('loads_live_on_purlins')} title={vtitle('loads_live_on_purlins')}>{bds1.loads_live_on_purlins} (KN/m2)</span>
                  </div>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-mb-2 tw-items-center">
                <div className="tw-col-span-5">On Frames:</div>
                <div className="tw-col-span-7">
                  <div className="tw-flex">
                    <span className={vcls('loads_on_frame')} title={vtitle('loads_on_frame')}>{bds1.loads_on_frame} (KN/m2)</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="tw-col-span-5">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-5">Collateral on Frame:</div>
                <div className="tw-col-span-7">
                  <div className="tw-flex">
                    <span className={vcls('loads_collateral_on_frame')} title={vtitle('loads_collateral_on_frame')}>{bds1.loads_collateral_on_frame} (KN/m2)</span>
                  </div>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-5">Collateral on Roof:</div>
                <div className="tw-col-span-7">
                  <div className="tw-flex">
                    <span className={vcls('loads_collateral_on_roof')} title={vtitle('loads_collateral_on_roof')}>{bds1.loads_collateral_on_roof} (KN/m2)</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="tw-col-span-2">
              <div className="tw-mt-3">
                <label className="tw-flex tw-items-center tw-gap-2">
                  <input
                    type="checkbox"
                    className={CHECK_DISPLAY_CLS}
                    checked={!!bds1.loads_seismic}
                    readOnly
                  />
                  <span className={mark(p('loads_seismic'))} title={vtitle('loads_seismic')}>Seismic</span>
                </label>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-2 tw-items-center">
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-mb-2 tw-items-center">
                <div className="tw-col-span-5">Wind Speed (KMH):</div>
                <div className="tw-col-span-7">
                  <span className={vcls('loads_wind_speed')} title={vtitle('loads_wind_speed')}>{bds1.loads_wind_speed} (KMH)</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-mb-2 tw-items-center">
                <div className="tw-col-span-5">Recurrence time:</div>
                <div className="tw-col-span-7">
                  <span className={vcls('loads_recurrence_time')} title={vtitle('loads_recurrence_time')}>{bds1.loads_recurrence_time}</span>
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Explosure:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('loads_explosure')} title={vtitle('loads_explosure')}>{bds1.loads_explosure}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Rainfall:</div>
                <div className="tw-col-span-9">
                  <div className="tw-flex tw-items-center">
                    <span className={vcls('loads_rainfall')} title={vtitle('loads_rainfall')}>{bds1.loads_rainfall} mm/hr</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-2 tw-items-center">
            <div className="tw-flex-1">
              <div className="tw-flex tw-gap-x-2 tw-mb-2 tw-items-center">
                <div>Description of collateral load:</div>
                <div className="tw-flex-1">
                  <span className={vcls('loads_description')} title={vtitle('loads_description')}>{bds1.loads_description}</span>
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-gap-x-2 tw-mb-2 tw-items-center">
                <div>Occupancy category:</div>
                <div className="tw-flex-1">
                  <span className={vcls('loads_occupancy_category')} title={vtitle('loads_occupancy_category')}>{bds1.loads_occupancy_category}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-5">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-font-semibold">Building Code:</div>
                <div className="tw-flex-1">
                  <div className="tw-flex tw-justify-between">
                    <label className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        className={CHECK_DISPLAY_CLS}
                        checked={!!bds1.loads_building_code_mbma_2012}
                        readOnly
                      />
                      <span className={mark(p('loads_building_code_mbma_2012'))} title={vtitle('loads_building_code_mbma_2012')}>MBMA 2012</span>
                    </label>

                    <label className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        className={CHECK_DISPLAY_CLS}
                        checked={!!bds1.loads_building_code_ibc_2018}
                        readOnly
                      />
                      <span className={mark(p('loads_building_code_ibc_2018'))} title={vtitle('loads_building_code_ibc_2018')}>IBC 2018</span>
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <DiffRichText path={p('loads_building_code_others')} html={bds1.loads_building_code_others} />
              </div>
            </div>
            <div className="tw-col-span-7">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-font-semibold">Design Standard:</div>
                <div className="tw-flex-1">
                  <div className="tw-flex tw-justify-between">
                    <label className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        className={CHECK_DISPLAY_CLS}
                        checked={!!bds1.loads_design_standard_aisc_asd89}
                        readOnly
                      />
                      <span className={mark(p('loads_design_standard_aisc_asd89'))} title={vtitle('loads_design_standard_aisc_asd89')}>AISC-ASD89</span>
                    </label>

                    <label className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        className={CHECK_DISPLAY_CLS}
                        checked={!!bds1.loads_design_standard_aisc_360_16}
                        readOnly
                      />
                      <span className={mark(p('loads_design_standard_aisc_360_16'))} title={vtitle('loads_design_standard_aisc_360_16')}>AISC360-16</span>
                    </label>

                    <label className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        className={CHECK_DISPLAY_CLS}
                        checked={!!bds1.loads_design_standard_aisi_2001}
                        readOnly
                      />
                      <span className={mark(p('loads_design_standard_aisi_2001'))} title={vtitle('loads_design_standard_aisi_2001')}>AISI 2001</span>
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <DiffRichText path={p('loads_design_standard_others')} html={bds1.loads_design_standard_others} />
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 3. Grade (kN/m2) */}
      <Accordion.Item eventKey="2" header="3. Grade (kN/m2)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
            <div className="tw-col-span-3">Primary member:</div>
            <div className="tw-col-span-9">
              <span className={vcls('grade_primary_member')} title={vtitle('grade_primary_member')}>{bds1.grade_primary_member}</span>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
            <div className="tw-col-span-3">Secondary member:</div>
            <div className="tw-col-span-9">
              <span className={vcls('grade_secondary_member')} title={vtitle('grade_secondary_member')}>{bds1.grade_secondary_member}</span>
            </div>
          </div>
          <div>
            <DiffRichText path={p('grade_note')} html={bds1.grade_note} />
          </div>
        </div>
      </Accordion.Item>

      {/* 4. Building Parameters */}
      <Accordion.Item eventKey="3" header="4. Building Parameters">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-4">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Frame Type:</div>
                <div className="tw-col-span-8">
                  <span className={vcls('building_parameters_frame_type')} title={vtitle('building_parameters_frame_type')}>{bds1.building_parameters_frame_type}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-3">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Slope:</div>
                <div className="tw-col-span-9">
                  <span className={vcls('building_parameters_slope')} title={vtitle('building_parameters_slope')}>{bds1.building_parameters_slope}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-5">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-3">Height (M):</div>
                <div className="tw-col-span-9">
                  <div className="tw-grid tw-grid-cols-12 tw-gap-x-1">
                    <div className="tw-col-span-6">
                      <span className={vcls('building_parameters_height_other')} title={vtitle('building_parameters_height_other')}>{bds1.building_parameters_height_other}</span>
                    </div>
                    <div className="tw-col-span-6">
                      <span className={vcls('building_parameters_height')} title={vtitle('building_parameters_height')}>{bds1.building_parameters_height}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-2">
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Bldg Width (M):</div>
                <div className="tw-col-span-8">
                  <span className={vcls('building_parameters_bldgs_width')} title={vtitle('building_parameters_bldgs_width')}>{bds1.building_parameters_bldgs_width}</span>
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Width modules:</div>
                <div className="tw-col-span-8">
                  <span className={vcls('building_parameters_width_module')} title={vtitle('building_parameters_width_module')}>{bds1.building_parameters_width_module}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-mb-3 tw-items-center">
            <div className="tw-col-span-2">Length (M):</div>
            <div className="tw-col-span-10">
              <span className={vcls('building_parameters_length')} title={vtitle('building_parameters_length')}>{bds1.building_parameters_length}</span>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-2">
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Bay Spacing (M):</div>
                <div className="tw-col-span-8">
                  <span className={vcls('building_parameters_bay_spacing')} title={vtitle('building_parameters_bay_spacing')}>{bds1.building_parameters_bay_spacing}</span>
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Columns support:</div>
                <div className="tw-col-span-8">
                  <span className={vcls('building_parameters_column_support')} title={vtitle('building_parameters_column_support')}>{bds1.building_parameters_column_support}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-mt-2">
            <DiffRichText path={p('building_parameters_note')} html={bds1.building_parameters_note} />
          </div>
        </div>
      </Accordion.Item>

      {/* 5. Endwall Condition */}
      <Accordion.Item eventKey="4" header="5. Endwall Condition">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2 tw-mb-2">
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Left End Wall Frame:</div>
                <div className="tw-col-span-8">
                  <span className={vcls('left_end_wall_frame')} title={vtitle('left_end_wall_frame')}>{bds1.left_end_wall_frame}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Right End Wall Frame:</div>
                <div className="tw-col-span-8">
                  <span className={vcls('right_end_wall_frame')} title={vtitle('right_end_wall_frame')}>{bds1.right_end_wall_frame}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Left End Girt type:</div>
                <div className="tw-col-span-8">
                  <span className={vcls('lef_end_girt_type')} title={vtitle('lef_end_girt_type')}>{bds1.lef_end_girt_type}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Near Side Girt type:</div>
                <div className="tw-col-span-8">
                  <span className={vcls('near_site_girt_type')} title={vtitle('near_site_girt_type')}>{bds1.near_site_girt_type}</span>
                </div>
              </div>
            </div>
            <div className="tw-flex-1">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Wind Col. Spacing (M):</div>
                <div className="tw-col-span-8">
                  <span className={vcls('wind_col_spacing_left')} title={vtitle('wind_col_spacing_left')}>{bds1.wind_col_spacing_left}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Wind Col. Spacing (M):</div>
                <div className="tw-col-span-8">
                  <span className={vcls('wind_col_spacing_right')} title={vtitle('wind_col_spacing_right')}>{bds1.wind_col_spacing_right}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Right End Girt type:</div>
                <div className="tw-col-span-8">
                  <span className={vcls('right_end_girt_type')} title={vtitle('right_end_girt_type')}>{bds1.right_end_girt_type}</span>
                </div>
              </div>
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2 tw-items-center">
                <div className="tw-col-span-4">Far Side Girt type:</div>
                <div className="tw-col-span-8">
                  <span className={vcls('far_side_girt_type')} title={vtitle('far_side_girt_type')}>{bds1.far_side_girt_type}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-mt-2">
            <DiffRichText path={p('endwall_condition_note')} html={bds1.endwall_condition_note} />
          </div>
        </div>
      </Accordion.Item>

      {/*6. Open walls lengths are measured from corner of NSW & LEW  */}
      <Accordion.Item eventKey="5" header="6. Open walls lengths are measured from corner of NSW & LEW">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-overflow-x-auto tw-text-sm tw-mt-2">
            <table className="tw-w-full tw-border-collapse tw-text-sm">
              <thead>
                <tr className="tw-text-center" style={{height: "10px"}}>
                  <th className={TH_CLS} rowSpan={2}>Location</th>
                  <th className={TH_CLS} colSpan={3}>Length (mm)</th>
                  <th className={TH_CLS} rowSpan={2}>Height (m)</th>
                  <th className={TH_CLS} rowSpan={2}>Open for</th>
                  <th className={TH_CLS} rowSpan={2}>Bracing Type</th>
                </tr>
                <tr className="tw-text-center">
                  <th className={TH_CLS}>Length</th>
                  <th className={TH_CLS}>From</th>
                  <th className={TH_CLS}>To</th>
                </tr>
              </thead>
              <tbody>
                {openWallData && openWallData.map((item, index) => (
                  <tr key={index} className={rmark('open_walls', item, index)}>
                    <td className={TD_CLS}>{item.location}</td>
                    <td className={TD_CLS}>{item.length}</td>
                    <td className={TD_CLS}>{item.length_from}</td>
                    <td className={TD_CLS}>{item.length_to}</td>
                    <td className={`${TD_CLS} tw-text-right`}>{item.height}</td>
                    <td className={TD_CLS}>{item.open_for}</td>
                    <td className={TD_CLS}>{item.bracing_type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="tw-mt-3 tw-mb-2">
            <DiffRichText path={p('open_walls_note')} html={bds1.open_walls_note} />
          </div>
        </div>
      </Accordion.Item>

      {/* 7. Roof */}
      <Accordion.Item eventKey="6" header="7. Roof">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-2">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.roof_by_pebsteel}
                  readOnly
                />
                <span className={mark(p('roof_by_pebsteel'))} title={vtitle('roof_by_pebsteel')}>by PEB Steel</span>
              </label>
            </div>
            <div className="tw-col-span-5">
              <div className="tw-flex tw-gap-x-2 tw-items-center">
                <div>Panel is:</div>
                <div className="tw-flex-1">
                  <span className={vcls('roof_panel_is')} title={vtitle('roof_panel_is')}>{bds1.roof_panel_is}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-5">
              <div className="tw-flex tw-gap-x-2 tw-items-center">
                <div>Profile:</div>
                <div className="tw-flex-1">
                  <span className={vcls('roof_profile')} title={vtitle('roof_profile')}>{bds1.roof_profile}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-2">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.roof_by_others}
                  readOnly
                />
                <span className={mark(p('roof_by_others'))} title={vtitle('roof_by_others')}>by Others</span>
              </label>
            </div>
            <div className="tw-col-span-10">
              <div className="tw-flex tw-gap-x-3 tw-items-center">
                <div>Color:</div>
                <div className="tw-flex-1">
                  <span className={vcls('roof_color')} title={vtitle('roof_color')}>{bds1.roof_color}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-border tw-border-solid tw-border-slate-200 tw-py-2">
            <div className="tw-col-span-2">Insulation</div>
            <div className="tw-col-span-10">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-3">
                <div className="tw-col-span-8">
                  <div>
                    <label className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        className={CHECK_DISPLAY_CLS}
                        checked={!!bds1.roof_insulation_by_pebsteel}
                        readOnly
                      />
                      <span className={mark(p('roof_insulation_by_pebsteel'))} title={vtitle('roof_insulation_by_pebsteel')}>by PEB Steel</span>
                    </label>
                  </div>
                  <div className="tw-flex tw-gap-x-2">
                    <div className="tw-flex-1">
                      <span className={vcls('roof_name')} title={vtitle('roof_name')}>{bds1.roof_name}</span>
                    </div>
                    <div className="tw-flex-1">
                      <div className="tw-flex tw-gap-x-2 tw-items-center">
                        <div>Thickness (mm):</div>
                        <div className="tw-flex-1">
                          <span className={vcls('roof_insulation_thickness')} title={vtitle('roof_insulation_thickness')}>{bds1.roof_insulation_thickness}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tw-col-span-4">
                  <div>
                    <label className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        className={CHECK_DISPLAY_CLS}
                        checked={!!bds1.roof_insulation_by_others}
                        readOnly
                      />
                      <span className={mark(p('roof_insulation_by_others'))} title={vtitle('roof_insulation_by_others')}>by Others</span>
                    </label>
                  </div>
                  <div className="tw-flex tw-gap-x-2 tw-items-center">
                    <div>Density:</div>
                    <div className="tw-flex-1">
                      <span className={vcls('roof_insulation_density')} title={vtitle('roof_insulation_density')}>{bds1.roof_insulation_density}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <DiffRichText path={p('roof_insulation_note')} html={bds1.roof_insulation_note} />
              </div>

            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-border tw-border-solid tw-border-slate-200 tw-py-2">
            <div className="tw-col-span-2">Skylight</div>
            <div className="tw-col-span-10">
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">
                  <label className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="checkbox"
                      className={CHECK_DISPLAY_CLS}
                      checked={!!bds1.roof_skylight_by_pebsteel}
                      readOnly
                    />
                    <span className={mark(p('roof_skylight_by_pebsteel'))} title={vtitle('roof_skylight_by_pebsteel')}>By Pebsteel</span>
                  </label>
                </div>
                <div className="tw-flex-1">
                  <label className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="checkbox"
                      className={CHECK_DISPLAY_CLS}
                      checked={!!bds1.roof_skylight_single_layer}
                      readOnly
                    />
                    <span className={mark(p('roof_skylight_single_layer'))} title={vtitle('roof_skylight_single_layer')}>Single layer</span>
                  </label>
                </div>
                <div className="tw-flex-1">
                  <label className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="checkbox"
                      className={CHECK_DISPLAY_CLS}
                      checked={!!bds1.roof_skylight_double_layer}
                      readOnly
                    />
                    <span className={mark(p('roof_skylight_double_layer'))} title={vtitle('roof_skylight_double_layer')}>Double layers</span>
                  </label>
                </div>
                <div className="tw-w-4/12">
                  <label className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="checkbox"
                      className={CHECK_DISPLAY_CLS}
                      checked={!!bds1.roof_skylight_by_others}
                      readOnly
                    />
                    <span className={mark(p('roof_skylight_by_others'))} title={vtitle('roof_skylight_by_others')}>By Others</span>
                  </label>
                </div>
              </div>
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">
                  <div>Width (mm):</div>
                  <span className={vcls('roof_skylight_width')} title={vtitle('roof_skylight_width')}>{bds1.roof_skylight_width}</span>
                </div>
                <div className="tw-flex-1">
                  <div>Length (mm):</div>
                  <span className={vcls('roof_skylight_length')} title={vtitle('roof_skylight_length')}>{bds1.roof_skylight_length}</span>
                </div>
                <div className="tw-flex-1">
                  <div>Thickness (mm):</div>
                  <span className={vcls('roof_skylight_thickness')} title={vtitle('roof_skylight_thickness')}>{bds1.roof_skylight_thickness}</span>
                </div>
                <div className="tw-flex-1">
                  <div>Color:</div>
                  <span className={vcls('roof_skylight_color')} title={vtitle('roof_skylight_color')}>{bds1.roof_skylight_color}</span>
                </div>
                <div className="tw-w-4/12">
                  <div>Description:</div>
                  <span className={vcls('roof_skylight_desciption')} title={vtitle('roof_skylight_desciption')}>{bds1.roof_skylight_desciption}</span>
                </div>
              </div>
              <div className="tw-mt-3">
                <DiffRichText path={p('roof_skylight_note')} html={bds1.roof_skylight_note} />
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 8. Wall */}
      <Accordion.Item eventKey="7" header="8. Wall">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-2">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.wall_by_pebsteel}
                  readOnly
                />
                <span className={mark(p('wall_by_pebsteel'))} title={vtitle('wall_by_pebsteel')}>by PEB Steel</span>
              </label>
            </div>
            <div className="tw-col-span-5">
              <div className="tw-flex tw-gap-x-2 tw-items-center">
                <div>Panel is:</div>
                <div className="tw-flex-1">
                  <span className={vcls('wall_panel_is')} title={vtitle('wall_panel_is')}>{bds1.wall_panel_is}</span>
                </div>
              </div>
            </div>
            <div className="tw-col-span-5">
              <div className="tw-flex tw-gap-x-2 tw-items-center">
                <div>Profile:</div>
                <div className="tw-flex-1">
                  <span className={vcls('wall_profile')} title={vtitle('wall_profile')}>{bds1.wall_profile}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-2">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.wall_by_others}
                  readOnly
                />
                <span className={mark(p('wall_by_others'))} title={vtitle('wall_by_others')}>by Others</span>
              </label>
            </div>
            <div className="tw-col-span-10">
              <div className="tw-flex tw-gap-x-3 tw-items-center">
                <div>Color:</div>
                <div className="tw-flex-1">
                  <span className={vcls('wall_color')} title={vtitle('wall_color')}>{bds1.wall_color}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-border tw-border-solid tw-border-slate-200 tw-py-2">
            <div className="tw-col-span-2">Insulation</div>
            <div className="tw-col-span-10">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-3">
                <div className="tw-col-span-8">
                  <div>
                    <label className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        className={CHECK_DISPLAY_CLS}
                        checked={!!bds1.wall_insulation_by_pebsteel}
                        readOnly
                      />
                      <span className={mark(p('wall_insulation_by_pebsteel'))} title={vtitle('wall_insulation_by_pebsteel')}>by Pebsteel</span>
                    </label>
                  </div>
                  <div className="tw-flex tw-gap-x-2">
                    <div className="tw-flex-1">
                      <span className={vcls('wall_name')} title={vtitle('wall_name')}>{bds1.wall_name}</span>
                    </div>
                    <div className="tw-flex-1">
                      <div className="tw-flex tw-gap-x-2 tw-items-center">
                        <div>Thickness (mm):</div>
                        <div className="tw-flex-1">
                          <span className={vcls('wall_insulation_thickness')} title={vtitle('wall_insulation_thickness')}>{bds1.wall_insulation_thickness}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="tw-col-span-4">
                  <div>
                    <label className="tw-flex tw-items-center tw-gap-2">
                      <input
                        type="checkbox"
                        className={CHECK_DISPLAY_CLS}
                        checked={!!bds1.wall_insulation_by_others}
                        readOnly
                      />
                      <span className={mark(p('wall_insulation_by_others'))} title={vtitle('wall_insulation_by_others')}>by Others</span>
                    </label>
                  </div>
                  <div className="tw-flex tw-gap-x-2 tw-items-center">
                    <div>Density:</div>
                    <div className="tw-flex-1">
                      <span className={vcls('wall_insulation_density')} title={vtitle('wall_insulation_density')}>{bds1.wall_insulation_density}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <DiffRichText path={p('wall_insulation_note')} html={bds1.wall_insulation_note} />
              </div>
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-border tw-border-solid tw-border-slate-200 tw-py-2">
            <div className="tw-col-span-2">Wall light</div>
            <div className="tw-col-span-10">
              <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
                <div className="tw-col-span-8">
                  <label className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="checkbox"
                      className={CHECK_DISPLAY_CLS}
                      checked={!!bds1.wall_light_by_pebsteel}
                      readOnly
                    />
                    <span className={mark(p('wall_light_by_pebsteel'))} title={vtitle('wall_light_by_pebsteel')}>By Pebsteel</span>
                  </label>
                </div>
                <div className="tw-col-span-4">
                  <label className="tw-flex tw-items-center tw-gap-2">
                    <input
                      type="checkbox"
                      className={CHECK_DISPLAY_CLS}
                      checked={!!bds1.wall_light_by_others}
                      readOnly
                    />
                    <span className={mark(p('wall_light_by_others'))} title={vtitle('wall_light_by_others')}>By Others</span>
                  </label>
                </div>
              </div>
              <div className="tw-flex tw-gap-x-2">
                <div className="tw-flex-1">
                  <div>Width (mm):</div>
                  <span className={vcls('wall_light_width')} title={vtitle('wall_light_width')}>{bds1.wall_light_width}</span>
                </div>
                <div className="tw-flex-1">
                  <div>Length (mm):</div>
                  <span className={vcls('wall_light_length')} title={vtitle('wall_light_length')}>{bds1.wall_light_length}</span>
                </div>
                <div className="tw-flex-1">
                  <div>Thickness (mm):</div>
                  <span className={vcls('wall_light_thickness')} title={vtitle('wall_light_thickness')}>{bds1.wall_light_thickness}</span>
                </div>
                <div className="tw-flex-1">
                  <div>Color:</div>
                  <span className={vcls('wall_light_color')} title={vtitle('wall_light_color')}>{bds1.wall_light_color}</span>
                </div>
                <div className="tw-w-4/12">
                  <div>Description:</div>
                  <span className={vcls('wall_light_description')} title={vtitle('wall_light_description')}>{bds1.wall_light_description}</span>
                </div>
              </div>
              <div className="tw-mt-3">
                <DiffRichText path={p('wall_light_note')} html={bds1.wall_light_note} />
              </div>
            </div>
          </div>
        </div>
      </Accordion.Item>

      {/* 9. Sheeting Angle, Gable Angle, Eave Angle (If sheeting by others) */}
      <Accordion.Item eventKey="8" header="9. Sheeting Angle, Gable Angle, Eave Angle (If sheeting by others)">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-2">
            <div className="tw-col-span-8">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.sheeting_angle_by_pebsteel}
                  readOnly
                />
                <span className={mark(p('sheeting_angle_by_pebsteel'))} title={vtitle('sheeting_angle_by_pebsteel')}>By Pebsteel</span>
              </label>
            </div>
            <div className="tw-col-span-4">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.sheeting_angle_by_others}
                  readOnly
                />
                <span className={mark(p('sheeting_angle_by_others'))} title={vtitle('sheeting_angle_by_others')}>By Others</span>
              </label>
            </div>
          </div>
          <div>
            <DiffRichText path={p('sheeting_angle_note')} html={bds1.sheeting_angle_note} />
          </div>
        </div>
      </Accordion.Item>

      {/* 10. Eave Condition */}
      <Accordion.Item eventKey="9" header="10. Eave Condition">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-flex tw-gap-x-2 tw-mb-2">
            <div className="tw-flex-1">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.eave_condition_gutter_downspouts}
                  readOnly
                />
                <span className={mark(p('eave_condition_gutter_downspouts'))} title={vtitle('eave_condition_gutter_downspouts')}>Gutters &amp; Downspouts,</span>
              </label>
            </div>
            <div className="tw-flex-1">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.eave_condition_trim}
                  readOnly
                />
                <span className={mark(p('eave_condition_trim'))} title={vtitle('eave_condition_trim')}>Eave Trim,</span>
              </label>
            </div>
            <div className="tw-flex-1"></div>
            <div className="tw-flex-1"></div>
          </div>
          <div className="tw-flex tw-gap-x-2 tw-mb-2">
            <div className="tw-flex-1">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.eave_condition_curved_projection}
                  readOnly
                />
                <span className={mark(p('eave_condition_curved_projection'))} title={vtitle('eave_condition_curved_projection')}>Curved Eave with projection,</span>
              </label>
            </div>
            <div className="tw-flex-1">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.eave_condition_w_o_gutters_downspouts}
                  readOnly
                />
                <span className={mark(p('eave_condition_w_o_gutters_downspouts'))} title={vtitle('eave_condition_w_o_gutters_downspouts')}>w/o Gutters &amp; Downspouts,</span>
              </label>
            </div>
            <div className="tw-flex-1">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.eave_condition_w_valley_gutters_downspouts}
                  readOnly
                />
                <span className={mark(p('eave_condition_w_valley_gutters_downspouts'))} title={vtitle('eave_condition_w_valley_gutters_downspouts')}>w/Valley Gutters &amp; Downspouts,</span>
              </label>
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-justify-between">
                <div>Downspouts</div>
                <label className="tw-flex tw-items-center tw-gap-2">
                  <input
                    type="checkbox"
                    className={CHECK_DISPLAY_CLS}
                    checked={!!bds1.eave_condition_downspouts_meta}
                    readOnly
                  />
                  <span className={mark(p('eave_condition_downspouts_meta'))} title={vtitle('eave_condition_downspouts_meta')}>Metal</span>
                </label>

                <label className="tw-flex tw-items-center tw-gap-2">
                  <input
                    type="checkbox"
                    className={CHECK_DISPLAY_CLS}
                    checked={!!bds1.eave_condition_downspouts_pvc}
                    readOnly
                  />
                  <span className={mark(p('eave_condition_downspouts_pvc'))} title={vtitle('eave_condition_downspouts_pvc')}>PVC</span>
                </label>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-gap-x-2">
            <div className="tw-flex-1">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.eave_condition_curved_projection2}
                  readOnly
                />
                <span className={mark(p('eave_condition_curved_projection2'))} title={vtitle('eave_condition_curved_projection2')}>Curved Eave w/o projection,</span>
              </label>
            </div>
            <div className="tw-flex-1">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.eave_condition_w_o_gutters_downspouts2}
                  readOnly
                />
                <span className={mark(p('eave_condition_w_o_gutters_downspouts2'))} title={vtitle('eave_condition_w_o_gutters_downspouts2')}>w/o Gutters &amp; Downspouts,</span>
              </label>
            </div>
            <div className="tw-flex-1">
              <label className="tw-flex tw-items-center tw-gap-2">
                <input
                  type="checkbox"
                  className={CHECK_DISPLAY_CLS}
                  checked={!!bds1.eave_condition_w_valley_gutters_downspouts2}
                  readOnly
                />
                <span className={mark(p('eave_condition_w_valley_gutters_downspouts2'))} title={vtitle('eave_condition_w_valley_gutters_downspouts2')}>w/Valley Gutters &amp; Downspouts,</span>
              </label>
            </div>
            <div className="tw-flex-1">
              <div className="tw-flex tw-justify-between">
                <div>Downspouts</div>
                <label className="tw-flex tw-items-center tw-gap-2">
                  <input
                    type="checkbox"
                    className={CHECK_DISPLAY_CLS}
                    checked={!!bds1.eave_condition_downspouts_meta2}
                    readOnly
                  />
                  <span className={mark(p('eave_condition_downspouts_meta2'))} title={vtitle('eave_condition_downspouts_meta2')}>Metal</span>
                </label>

                <label className="tw-flex tw-items-center tw-gap-2">
                  <input
                    type="checkbox"
                    className={CHECK_DISPLAY_CLS}
                    checked={!!bds1.eave_condition_downspouts_pvc2}
                    readOnly
                  />
                  <span className={mark(p('eave_condition_downspouts_pvc2'))} title={vtitle('eave_condition_downspouts_pvc2')}>PVC</span>
                </label>
              </div>
            </div>
          </div>
          <div className="tw-mt-2">
            <DiffRichText path={p('eave_condition_note')} html={bds1.eave_condition_note} />
          </div>
        </div>
      </Accordion.Item>

      {/* 11. Wall Trim Colors */}
      <Accordion.Item eventKey="10" header="11. Wall Trim Colors">
        <div className={SECTION_BODY_CLS}>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2 tw-mb-3">
            <div className="tw-col-span-4">
              <div>Color of gutters, down spouts, Eave trim :</div>
              <span className={vcls('wall_trim_colors')} title={vtitle('wall_trim_colors')}>{bds1.wall_trim_colors}</span>
            </div>
            <div className="tw-col-span-8">
              <DiffRichText path={p('wall_trim_colors_gutters_note')} html={bds1.wall_trim_colors_gutters_note} />
            </div>
          </div>
          <div className="tw-grid tw-grid-cols-12 tw-gap-x-2">
            <div className="tw-col-span-4">
              <div>Color of other flashing, trim:</div>
              <span className={vcls('wall_trim_colors_others')} title={vtitle('wall_trim_colors_others')}>{bds1.wall_trim_colors_others}</span>
            </div>
            <div className="tw-col-span-8">
              <DiffRichText path={p('wall_trim_colors_flashing_note')} html={bds1.wall_trim_colors_flashing_note} />
            </div>
          </div>
        </div>
      </Accordion.Item>
    </Accordion>
  )
};

DetailBDS1.propTypes = {
  project: PropTypes.object.isRequired,
  building: PropTypes.object.isRequired,
  adaptation: PropTypes.object.isRequired,
  area: PropTypes.object.isRequired,
  areaIndex: PropTypes.number
}

export default DetailBDS1;
